import React, { useState, useEffect } from 'react';
import { users, teams } from '../services/api';
import { nomUsuari, inicialUsuari } from '../utils/usuari';

function UsersPage() {
  const [activeTab, setActiveTab] = useState('users');
  const [userList, setUserList] = useState([]);
  const [pendingList, setPendingList] = useState([]);
  const [teamList, setTeamList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState({ role: '', search: '' });

  // Team management state
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'analyst'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, teamsRes, pendingRes] = await Promise.all([
        users.getAll(),
        teams.getAll(),
        users.getPending()
      ]);
      setUserList(usersRes.data.data || usersRes.data);
      setTeamList(teamsRes.data);
      setPendingList(pendingRes.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant les dades');
    } finally {
      setLoading(false);
    }
  };

  // User functions
  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'analyst'
    });
    setShowModal(true);
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        delete updateData.email;
        await users.update(editingUser.id, updateData);
      } else {
        await users.create(formData);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'L\'operació ha fallat');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Estàs segur que vols desactivar aquest usuari?')) return;
    try {
      await users.deactivate(userId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error desactivant l\'usuari');
    }
  };

  /**
   * Esborrat definitiu, en dos temps.
   *
   * Primer es prova sense res: si el compte no té feina, marxa i s'acaba aquí.
   * El servidor només demana cerimònia quan hi ha alguna cosa a destruir, i és
   * ell qui decideix —no aquesta pantalla—, que és el que evita que un canvi
   * al client es salti la protecció.
   *
   * Si té exercicis, respon 409 i llavors sí que es demana escriure el nom.
   * Amb comptes que es diuen «Grup 01», «Grup 02»… clicar la fila equivocada és
   * fàcil i aquí no hi ha desfer.
   */
  const handleEsborrar = async (user) => {
    const nom = nomUsuari(user);
    if (!window.confirm(`Esborrar definitivament «${nom}»?\n\nNo és el mateix que desactivar: la fila desapareix i no es pot desfer.`)) return;

    try {
      await users.esborrar(user.id);
      loadData();
    } catch (err) {
      const dades = err.response?.data;

      if (dades?.code === 'HAS_WORK') {
        const escrit = window.prompt(
          `${dades.message}\n\nSi vols continuar, escriu el nom exacte del compte:`
        );
        if (escrit === null) return;

        try {
          await users.esborrar(user.id, { ambFeina: true, confirmacio: escrit });
          loadData();
        } catch (err2) {
          setError(err2.response?.data?.message || 'Error esborrant l\'usuari');
        }
        return;
      }

      // DELETE_BLOCKED i la resta: el missatge del servidor ja diu què ho impedeix.
      setError(dades?.message || 'Error esborrant l\'usuari');
    }
  };

  // Pending users functions
  const handleApprove = async (userId) => {
    try {
      await users.approve(userId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error aprovant l\'usuari');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Estàs segur que vols rebutjar aquesta sol·licitud? L\'usuari serà eliminat.')) return;
    try {
      await users.reject(userId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error rebutjant l\'usuari');
    }
  };

  // Team functions
  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Estàs segur que vols eliminar aquest equip?')) return;
    try {
      await teams.delete(teamId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant l\'equip');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedTeam || !selectedUserId) return;

    try {
      setSubmitting(true);
      await teams.addMember(selectedTeam.id, { userId: selectedUserId });
      setShowAddMemberModal(false);
      setSelectedUserId('');
      setSelectedTeam(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error afegint membre');
    } finally {
      setSubmitting(false);
    }
  };

  // Get analysts available to add to the selected team
  const getAvailableAnalysts = () => {
    if (!selectedTeam) return [];
    const memberIds = selectedTeam.members?.map(m => m.userId) || [];
    return userList.filter(u => u.role === 'analyst' && !memberIds.includes(u.id));
  };

  const handleRemoveMember = async (teamId, userId) => {
    if (!window.confirm('Eliminar aquest membre de l\'equip?')) return;
    try {
      await teams.removeMember(teamId, userId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error eliminant membre');
    }
  };

  const filteredUsers = userList.filter(user => {
    if (filter.role && user.role !== filter.role) return false;
    if (filter.search) {
      const search = filter.search.toLowerCase();
      return (
        (user.email || '').toLowerCase().includes(search) ||
        (user.displayName || '').toLowerCase().includes(search) ||
        user.firstName?.toLowerCase().includes(search) ||
        user.lastName?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const roleColors = {
    admin: 'bg-info-subtle text-info-text',
    instructor: 'bg-accent-subtle text-accent',
    analyst: 'bg-success-subtle text-success-text'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">Gestió</h1>
          <p className="text-content-secondary">Gestiona usuaris i equips</p>
        </div>
        {activeTab === 'users' && (
          <button onClick={handleOpenCreate} className="btn btn-primary">
            Afegir usuari
          </button>
        )}
      </div>

      {error && (
        <div className="bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-error">×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-primary-500 text-accent'
                : 'border-transparent text-content-muted hover:text-content-secondary hover:border-border-strong'
            }`}
          >
            Usuaris ({userList.length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-2 px-1 border-b-2 font-medium text-sm relative ${
              activeTab === 'pending'
                ? 'border-primary-500 text-accent'
                : 'border-transparent text-content-muted hover:text-content-secondary hover:border-border-strong'
            }`}
          >
            Pendents d'aprovar
            {pendingList.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-amber-500 text-white rounded-full">
                {pendingList.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('teams')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'teams'
                ? 'border-primary-500 text-accent'
                : 'border-transparent text-content-muted hover:text-content-secondary hover:border-border-strong'
            }`}
          >
            Equips ({teamList.length})
          </button>
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Filters */}
          <div className="card">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <input
                  type="text"
                  placeholder="Cerca per nom o correu..."
                  value={filter.search}
                  onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <select
                  value={filter.role}
                  onChange={(e) => setFilter({ ...filter, role: e.target.value })}
                  className="input"
                >
                  <option value="">Tots els rols</option>
                  <option value="admin">Administrador</option>
                  <option value="instructor">Instructor</option>
                  <option value="analyst">Analista</option>
                </select>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="card overflow-hidden">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-subtle">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">Usuari</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">Rol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">Estat</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">Creat</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-content-muted uppercase tracking-wider">Accions</th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-border">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-content-muted">No s'han trobat usuaris</td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-surface-subtle">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-accent-subtle flex items-center justify-center text-accent font-medium">
                            {inicialUsuari(user)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-content">
                              {nomUsuari(user)}
                            </div>
                            <div className="text-sm text-content-muted">
                              {user.email || (user.isExamAccount ? "Compte de grup · entra amb codi d'examen" : '—')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleColors[user.role]}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.isActive !== false ? 'bg-success-subtle text-success-text' : 'bg-error-subtle text-error-text'
                        }`}>
                          {user.isActive !== false ? 'Actiu' : 'Inactiu'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-content-muted">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button onClick={() => handleOpenEdit(user)} className="text-accent hover:text-accent-hover mr-3">
                          Editar
                        </button>
                        {user.isActive !== false && (
                          <button onClick={() => handleDeactivate(user.id)} className="text-error hover:text-error-text mr-3">
                            Desactivar
                          </button>
                        )}
                        {/* «Esborrar» va a part i amb un altre pes visual que
                            «Desactivar»: són accions molt diferents i abans
                            només n'hi havia una. Desactivar és reversible;
                            això no. */}
                        <button
                          onClick={() => handleEsborrar(user)}
                          className="text-content-muted hover:text-error underline decoration-dotted underline-offset-2"
                          title="Esborrat definitiu: la fila desapareix"
                        >
                          Esborrar
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card text-center">
              <p className="text-3xl font-bold text-content">{userList.length}</p>
              <p className="text-sm text-content-muted">Total usuaris</p>
            </div>
            <div className="card text-center">
              <p className="text-3xl font-bold text-info">{userList.filter(u => u.role === 'admin').length}</p>
              <p className="text-sm text-content-muted">Administradors</p>
            </div>
            <div className="card text-center">
              <p className="text-3xl font-bold text-accent">{userList.filter(u => u.role === 'instructor').length}</p>
              <p className="text-sm text-content-muted">Instructors</p>
            </div>
            <div className="card text-center">
              <p className="text-3xl font-bold text-success">{userList.filter(u => u.role === 'analyst').length}</p>
              <p className="text-sm text-content-muted">Analistes</p>
            </div>
          </div>
        </>
      )}

      {/* Pending Tab */}
      {activeTab === 'pending' && (
        <>
          {pendingList.length === 0 ? (
            <div className="card text-center py-12">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-success-subtle rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-content-muted">No hi ha sol·licituds pendents d'aprovació</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingList.map((user) => (
                <div key={user.id} className="card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-full bg-warning-subtle flex items-center justify-center text-warning font-medium text-lg">
                        {inicialUsuari(user)}
                      </div>
                      <div>
                        <p className="font-medium text-content">
                          {nomUsuari(user)}
                        </p>
                        <p className="text-sm text-content-muted">
                          {user.email || (user.isExamAccount ? "Compte de grup · entra amb codi d'examen" : '—')}
                        </p>
                        <p className="text-xs text-content-disabled mt-1">
                          Sol·licitat el {new Date(user.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleApprove(user.id)}
                        className="btn bg-green-600 hover:bg-green-700 text-white"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Aprovar
                      </button>
                      <button
                        onClick={() => handleReject(user.id)}
                        className="btn btn-danger"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Rebutjar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Teams Tab */}
      {activeTab === 'teams' && (
        <>
          {teamList.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-content-muted">Encara no hi ha equips. Els equips es creen en iniciar avaluacions basades en equip.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {teamList.map((team) => (
                <div key={team.id} className="card">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-content">{team.name}</h3>
                      <p className="text-sm text-content-muted">
                        {team.projectTitle} / {team.caseTitle}
                      </p>
                      <p className="text-xs text-content-disabled mt-1">
                        Creat el {new Date(team.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedTeam(team);
                          setShowAddMemberModal(true);
                        }}
                        className="btn btn-secondary text-sm"
                      >
                        Afegir membre
                      </button>
                      <button
                        onClick={() => handleDeleteTeam(team.id)}
                        className="btn btn-danger text-sm"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>

                  {/* Team Members */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-content-secondary mb-2">
                      Membres ({team.members?.length || 0})
                    </h4>
                    {team.members?.length === 0 ? (
                      <p className="text-sm text-content-muted">Encara no hi ha membres</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {team.members?.map((member) => (
                          <div
                            key={member.userId}
                            className="flex items-center bg-surface-muted rounded-full px-3 py-1"
                          >
                            <span className="text-sm text-content-secondary">
                              {nomUsuari(member)}
                            </span>
                            {member.role === 'leader' && (
                              <span className="ml-1 text-xs text-warning">★</span>
                            )}
                            <button
                              onClick={() => handleRemoveMember(team.id, member.userId)}
                              className="ml-2 text-content-disabled hover:text-error"
                              title="Eliminar membre"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-content mb-4">
                {editingUser ? 'Editar usuari' : 'Crear nou usuari'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary">Correu electrònic</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input mt-1"
                    disabled={!!editingUser}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content-secondary">
                    Contrasenya {editingUser && '(deixa en blanc per mantenir l\'actual)'}
                  </label>
                  <input
                    type="password"
                    required={!editingUser}
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Nom</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="input mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-content-secondary">Cognoms</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="input mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-content-secondary">Rol</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="input mt-1"
                  >
                    <option value="analyst">Analista</option>
                    <option value="instructor">Instructor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    Cancel·lar
                  </button>
                  <button type="submit" disabled={submitting} className="btn btn-primary disabled:opacity-50">
                    {submitting ? 'Desant...' : (editingUser ? 'Actualitzar' : 'Crear')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && selectedTeam && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-content mb-4">
                Afegir membre a {selectedTeam.name}
              </h2>
              <form onSubmit={handleAddMember} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content-secondary">Selecciona un analista</label>
                  {getAvailableAnalysts().length === 0 ? (
                    <p className="mt-2 text-sm text-content-muted">
                      No hi ha analistes disponibles per afegir a aquest equip.
                    </p>
                  ) : (
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="input mt-1 w-full"
                      required
                    >
                      <option value="">Selecciona un analista...</option>
                      {getAvailableAnalysts().map(user => (
                        <option key={user.id} value={user.id}>
                          {user.email} {user.firstName && `(${user.firstName} ${user.lastName})`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMemberModal(false);
                      setSelectedUserId('');
                      setSelectedTeam(null);
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel·lar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !selectedUserId}
                    className="btn btn-primary disabled:opacity-50"
                  >
                    {submitting ? 'Afegint...' : 'Afegir'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersPage;
